<template>
  <div id="manning-schedule-crew-container" class="manning-schedule-crew-wrap">
    <scheduler-project-model
      ref="projectmodel"
      :hasScheduleChangedAsync="SchedulerEventHandler"
      :syncFailAsync="syncFailAsync"
      :loadFailAsync="loadFailAsync"
      :eventStore="CrewEventStore"
      :resourceStore="CrewResourceStore"
      :assignmentStore="CrewAssignmentStore"
      :timeRangeStore="CrewTimeRangeStore"
      :fiscalYear="selectedYear"
      :transport="transport"
      :syncSuccessAsync="syncSuccessAsync"
    />

    <!-- スケジューラPro -->
    <bryntum-scheduler-pro
      v-if="schedulerConfig"
      v-bind="schedulerConfig"
      :autoCreate="false"
      cls="aisea-scheduler"
      :taskEditFeature="taskEditFeature"
      :eventTooltipFeature="eventTooltipFeature"
      :scheduleContextFeature="scheduleContextFeature"
      :eventMenuFeature="eventMenuFeature"
      :eventLayout="eventLayout"
      :eventRenderer="eventRenderer"
      :columns="columns"
      :listeners="listeners"
      :project="project"
      :tbar="tbar"
      ref="scheduler"
      :subGridConfigs="subGridConfigs"
    >
    </bryntum-scheduler-pro>

    <manning-vessel-crew-info ref="manningVesselCrewInfo" />
  </div>
</template>
<script>
import { BryntumSchedulerPro } from "@bryntum/schedulerpro-vue";
import { LocaleManager, LocaleHelper } from "@bryntum/schedulerpro";
import "@bryntum/schedulerpro/locales/schedulerpro.locale.Ja.js";

import moment from "moment";

import C from "../Constants";
import transport from "./Transport";

import SchedulerEventHandler from "../scheduler-edit-event-handler";
import {
  CrewEventStore,
  CrewResourceStore,
  CrewAssignmentStore,
  CrewTimeRangeStore,
  crewResourceSorter,
} from "./scheduler-stores";

import { createSchedulerConfig } from "../scheduler-config/crew";

import EventHelperCrew from "../scheduler-event-helper/crew";

import { getVisibleDate } from "../utils/SchedulerUtil";

import CustomTaskEdit from "./components/scheduler/CustomTaskEdit";
import Toolbar from "../components/scheduler/Toolbar";
import EventTooltipFeature from "../components/scheduler/features/EventTooltipFeature";
import EventRenderer from "../components/scheduler/renderer/EventRenderer";
import { ViewPresetsHandler } from "../scheduler-config/viewPreset";

// マスターデータのサービス
import {
  // 船員基本条件マスター
  EnterpriseCrewsBasicConditionsService,
  // 船舶情報マスター
  EnterpriseManningVesselsService,
  // 法人勤務種別サービス
  EnterpriseManningStatusService,
  EnterpriseCrewsService,
  EnterpriseCrewsFiscalYeatDefaultsService,
  EnterpriseCrewSchedulerService,
  EnterpriseCrewRoleService,
} from "../services/master";
import { getEmploymentStatusLabel } from "../../../../management/crews/crew-settings/constants/EmploymentStatus.js";

import { VesselRolePositonAssignableService } from "../services/crews/VesselRolePositonAssignableService";

import SchedulerProjectModel from "../SchedulerProjectModel.vue";
import ManningVesselCrewInfo from "../components/manning-vessel-crew-info/ManningVesselCrewInfo.vue";

/**
 * 集計表情報を更新する
 * @param resources リソースデータの配列
 * @param resourceStore リソースストア
 * @param scheduler スケジューラインスタンス
 */
const updateSummariesToResource = (resources, resourceStore, scheduler) => {
  const targetRefreshModels = [];

  // リソースモデルのサマリー情報を更新
  resources.forEach(resource => {
    const summaryMap = {};

    if (Array.isArray(resource.summaries)) {
      resource.summaries.forEach(s => {
        summaryMap[s.column_id] = s.column_value;
      });
    }

    const resourceModel = resourceStore.getById(resource.id);
    if (!resourceModel) {
      return;
    }

    resourceModel.set("summaries", resource.summaries, true /* silent */);

    Object.keys(summaryMap).forEach(key => {
      const value = summaryMap[key];
      resourceModel.set(key, value, true /* silent */);
    });

    targetRefreshModels.push(resourceModel);
  });

  // リソースストアのコミット
  resourceStore.commit(true /* silent */);
  // 対象リソースの表示を更新
  scheduler.refreshResources(targetRefreshModels);
};

export default {
  name: "Crews",
  components: {
    BryntumSchedulerPro,
    SchedulerProjectModel,
    ManningVesselCrewInfo,
  },
  data() {
    return {
      SchedulerEventHandler: SchedulerEventHandler(C.DISPLAY_MODE.CREWS),
      subGridConfigs: {
        left: {
          minWidth: 300,
        },
      },
      // 現在日時
      nowDate: null,
      // スケジューラ設定
      schedulerConfig: null,
      currentStartDate: null,
      currentEndDate: null,
      // TaskEditのセレクトボックスに表示する下船中様態ステータス一覧
      selectableAhoreStausList: [],
      // Project設定
      project: null,
      transport: transport,
      // スケジューラ設定
      // 年度選択
      selectedYear: null,
      // ヘッダーのタブバー表示設定
      tbar: null,
      // リソース列表示設定
      columns: this.buildColumns(),
      // スケジューラのイベントレンダラー
      eventRenderer: null,
      // スケジューラのイベントリスナー
      listeners: {
        beforeTaskEditShow: CustomTaskEdit.handleBeforeTaskEditShow,
      },
      // イベント表示設定
      eventLayout: {
        type: "stack",
        groupBy: event => {
          if (
            event.scheduling_status === C.SCHEDULING_STATUS.SCHEDULED &&
            event.event_type === C.SCHEDULING_EVENT_TYPE.DAY_OFF_REQUEST
          ) {
            return 1;
          }

          if (event.scheduling_status === C.SCHEDULING_STATUS.PLANNING) {
            return 2;
          }
          return 0;
        },
      },
      // スケジューラのセル設定
      // セルをマウスオーバしたときに、「...」アイコンを表示
      // クリックした時に、メニュー表示
      scheduleContextFeature: {
        activateOnEvent: true,
        triggerEvent: "hover",
        widget: {
          type: "button",
          cls: "context-button b-blue b-transparent",
          icon: "b-fa b-fa b-fa-ellipsis",
          menuIcon: null,
          menu: {
            items: {
              addOnboard: {
                icon: "b-fa b-fa-edit",
                text: "乗船期間",
                onItem: async () => {
                  // クリックしたセルのリソースと日時を取得
                  const timelineContext = this.$refs.scheduler.instance
                    .timelineContext;
                  const resource = timelineContext.resourceRecord;
                  const startDate = timelineContext.date;
                  // 日時から年度を取得
                  const fiscalYear = EnterpriseCrewsBasicConditionsService.getFiscalYear(
                    startDate
                  );

                  // 基本乗船日数を取得
                  // クリックしたセルのリソースのCrew IDを取得
                  const crewId = resource.id.split("_")[1]; // 例: "c_123" の場合、123を取得
                  let basicBoardingDays = EnterpriseCrewsService.getBasicBoardingDaysByFiscalYear(
                    crewId,
                    fiscalYear
                  );
                  if (!basicBoardingDays) {
                    basicBoardingDays = EnterpriseCrewsFiscalYeatDefaultsService.getBasicBoardingDays(
                      fiscalYear
                    );
                  }
                  // 基本乗船日数が取得できなければ、0を設定
                  if (!basicBoardingDays) {
                    basicBoardingDays = 0;
                  }
                  const endDate = moment(startDate)
                    .add(basicBoardingDays, "days")
                    .toDate();

                  // 乗船期間新規追加時の乗船可能船舶とそれに紐づく役職ポジションを設定するためのセレクトボックス用データ作成
                  // 乗船可能な船舶を取得
                  const assaignableVessels = await EnterpriseManningVesselsService.fetchAssignable(
                    startDate,
                    crewId
                  );
                  // セレクトボックス用のデータを作成
                  const selectable = VesselRolePositonAssignableService.createVesselRolePositionSelectable(
                    assaignableVessels,
                    EnterpriseManningVesselsService.getEnterpriseManningVessels()
                  );

                  if (selectable.length === 0) {
                    this.$vs.notify({
                      text: `乗船可能な船舶がありません`,
                      color: "danger",
                      iconPack: "feather",
                      icon: "icon-alert-circle",
                    });
                    return;
                  }

                  const vesselEvent = EventHelperCrew.newVesselEvent(
                    startDate,
                    endDate,
                    selectable[0].vessel.value,
                    selectable[0].rolePositions[0].value
                  );

                  CustomTaskEdit.updateVesselRolePositionCombo(selectable);

                  // イベント編集ツールチップを表示
                  this.$refs.scheduler.instance.editEvent(
                    vesselEvent,
                    resource
                  );
                },
              },
              addOffboard: {
                icon: "b-fa b-fa-arrow-right-from-bracket",
                text: "下船期間",
                onItem: () => {
                  // クリックしたセルのリソースと日時を取得
                  const timelineContext = this.$refs.scheduler.instance
                    .timelineContext;
                  const resource = timelineContext.resourceRecord;
                  const startDate = timelineContext.date;
                  const endDate = moment(startDate)
                    .add(1, "days")
                    .toDate();

                  // 辞令職IDはを検索する。
                  const crewId = resource.id.split("_")[1];
                  const crew = EnterpriseCrewsService.findById(crewId);
                  const crewRole = EnterpriseCrewsService.findAppointmentRoleByDate(
                    crew,
                    startDate
                  );

                  // 辞令職が見つからない場合は、通知を表示して終了
                  if (!crewRole) {
                    this.$vs.notify({
                      text: `指定された日付に${crew.name}の辞令職が見つかりませんでした。`,
                      color: "danger",
                      iconPack: "feather",
                      icon: "icon-alert-circle",
                    });
                    return;
                  }

                  const enterprise_crew_role_id =
                    crewRole.enterprise_crew_role_id;
                  // 下船中様態のステータスのデフォルト設定(最初のステータスを設定)
                  const statusId = this.selectableAhoreStausList[0].id;
                  const eventModel = EventHelperCrew.newAshoreEvent(
                    startDate,
                    endDate,
                    statusId,
                    enterprise_crew_role_id
                  );
                  // イベント編集ツールチップを表示
                  this.$refs.scheduler.instance.editEvent(eventModel, resource);
                },
              },
              addDayOffRequest: {
                icon: "b-fa b-fa-calendar-days",
                text: "休暇希望",
                onItem: () => {
                  // クリックしたセルのリソースと日時を取得
                  const timelineContext = this.$refs.scheduler.instance
                    .timelineContext;
                  const resource = timelineContext.resourceRecord;
                  const startDate = timelineContext.date;
                  const endDate = moment(startDate)
                    .add(1, "days")
                    .toDate();

                  const eventModel = EventHelperCrew.newDayOffRequestEvent(
                    startDate,
                    endDate
                  );
                  // イベント編集ツールチップを表示
                  this.$refs.scheduler.instance.editEvent(eventModel, resource);
                },
              },
            },
          },
        },
      },
      // イベントを右クリックしたときのメニュー表示設定
      // weightはメニューの表示順
      eventMenuFeature: {
        items: {
          deleteEvent: {
            text: "削除",
            icon: "b-fa b-fa-trash",
            weight: 3,
            onItem: ({ eventRecord }) => {
              // イベントストアを取得
              const scheduler = this.$refs.scheduler.instance;
              const eventStore = scheduler.eventStore;

              // 予定イベントの削除は確認ダイアログを表示
              if (
                eventRecord.scheduling_status === C.SCHEDULING_STATUS.SCHEDULED
              ) {
                this.$vs.dialog({
                  type: "confirm",
                  color: "danger",
                  title: "スケジュールの削除",
                  text: "削除しますがよろしいですか",
                  cancelText: "キャンセル",
                  acceptText: "OK",
                  accept: () => {
                    // 予定イベントの削除
                    eventStore.remove(eventRecord);
                  },
                });
              } else {
                // 予定以外のイベントは確認なしで削除
                eventStore.remove(eventRecord);
              }
            },
          },
          editEvent: {
            text: "編集",
            icon: "b-fa b-fa-edit",
            weight: 2,
          },
          editVesselInfo: {
            text: "乗船情報編集",
            icon: "b-fa b-fa-ship",
            onItem: async ({ eventRecord }) => {
              await this.$refs.manningVesselCrewInfo.open(
                eventRecord,
                this.manningVessels,
                this.crews
              );
            },
            weight: 1,
          },

          splitEvent: false,
          cutEvent: false,
          copyEvent: false,
          unassignEvent: false,
        },
        processItems({ eventRecord, items }) {
          // 乗船期間編集イベントの場合は、乗船情報編集を表示
          items.editVesselInfo.hidden =
            eventRecord.event_type !== C.SCHEDULING_EVENT_TYPE.VESSEL;
          items.editEvent.text =
            eventRecord.event_type === C.SCHEDULING_EVENT_TYPE.VESSEL
              ? "期間編集"
              : "編集";
        },
      },
      // イベントマウスオーバ時のツールチップ表示設定
      eventTooltipFeature: {},
      taskEditFeature: {},
      schedulerColumns: [],
    };
  },
  setup() {
    return {
      CrewEventStore,
      CrewResourceStore,
      CrewAssignmentStore,
      CrewTimeRangeStore,
    };
  },
  methods: {
    /**
     * リソース列の設定
     * @returns {Array} リソース列の設定
     */
    buildColumns() {
      return [
        {
          text: "辞令職",
          id: "appointed_role_name",
          field: "appointed_role_name",
          htmlEncode: false,
          readOnly: true,
          // 6文字 fontsize: 14 * 6文字 + 余白: 14 + 14
          width: 112,
          region: "left",
          renderer: ({ record, value, cellElement }) => {
            if (record && record.isLeaf && cellElement) {
              const rowEl =
                cellElement.closest &&
                (cellElement.closest(".b-grid-row") ||
                  cellElement.closest("tr"));
              if (rowEl) {
                rowEl.classList.toggle("retired-crew-row", !!record.isRetired);
              }
            }
            return value;
          },
          filterable: {
            filterField: {
              type: "combo",
              multiSelect: true,
              editable: false,
              operator: "isIncludedIn",
              items: [],
            },
          },
        },
        {
          text: "氏名",
          field: "name",
          id: "name",
          htmlEncode: false,
          editor: false,
          readOnly: true,
          // 7文字 fontsize: 14 * 7文字 + 余白: 14 + 14
          width: 126,
          region: "left",
          renderer: ({ record, value, cellElement }) => {
            const isRetired = record.isLeaf && record.isRetired;
            if (cellElement) {
              const rowEl =
                cellElement.closest &&
                (cellElement.closest(".b-grid-row") ||
                  cellElement.closest("tr"));
              if (rowEl) {
                rowEl.classList.toggle("retired-crew-row", !!isRetired);
              }
            }
            if (isRetired) {
              return `${value}<span class="retired-label">[退職]</span>`;
            }
            return value;
          },
          filterable: {
            filterField: {
              type: "combo",
              multiSelect: true,
              operator: "isIncludedIn",
              items: [],
            },
          },
        },
        {
          text: "社員区分",
          id: "employment_status",
          field: "employment_status",
          htmlEncode: false,
          editor: false,
          readOnly: true,
          // 4文字 fontsize: 14 * 4文字 + 余白: 14 + 14
          width: 84,
          region: "left",
          filterable: {
            filterField: {
              type: "combo",
              multiSelect: true,
              operator: "isIncludedIn",
              items: [],
            },
          },
        },
      ];
    },

    /**
     * 年度が変更されたときの処理
     * @param {number} year 選択された年度
     */
    async onFiscalYearChange(year) {
      // 年度が変更されたときの処理
      this.selectedYear = year;
      // スケジューラのデータを再読み込み
      await this.reloadData();
    },

    /**
     *  スケジューラの同期成功時の処理
     * @param res 同期成功時のレスポンス
     */
    async syncSuccessAsync(res) {
      // 集計情報を更新
      const resourceStore = this.project.instance.resourceStore;
      const resource = res.resource;
      updateSummariesToResource(
        [resource],
        resourceStore,
        this.$refs.scheduler.instance
      );
    },

    /**
     * スケジューラの同期失敗時の処理
     * こちらはSchedulerProjectModelから呼び出されるイベントハンドラ
     * @param {Object} response レスポンスオブジェクト
     */
    async syncFailAsync(response) {
      // 同期失敗時の処理
      const text =
        response && response.message
          ? response.message
          : "データを更新できませんでした。再読込みして下さい。";
      this.$vs.dialog({
        type: "confirm",
        color: "danger",
        title: "更新に失敗しました",
        text: text,
        buttonCancel: true,
        acceptText: "再読込み",
        accept: () => {
          // ページをリロード
          this.reloadData();
        },
      });
    },

    /**
     * スケジューラのロード失敗時の処理
     * こちらはSchedulerProjectModelから呼び出されるイベントハンドラ
     * @param {Object} response レスポンスオブジェクト
     */
    async loadFailAsync() {
      // ロード失敗時の処理
      this.$vs.dialog({
        type: "confirm",
        color: "danger",
        title: "システムエラー",
        text: "システムエラーが発生しました。管理者へお問い合わせください。",
        buttonCancel: true,
        acceptText: "OK",
      });
    },

    async reloadData() {
      // スケジューラのデータを取得
      this.project = this.$refs.projectmodel.project;
      const projectInstance = this.project.instance;

      // 次の条件になると、SchedulerProの内部でエラーが発生するため、一度ダミーデータで上書き後にロードする。
      // 1. リソース、イベント、アサインメントが空の年度データをロード
      // 2. その後、リソース、イベント、アサインメントが入っている年度のデータをロード
      projectInstance.loadCrudManagerData({
        success: true,
        resources: {
          rows: [
            {
              id: "dummy",
              name: "",
            },
          ],
        },
        events: {
          rows: [],
        },
        assignments: {
          rows: [],
        },
      });

      const res = await projectInstance.load({
        request: {
          params: {
            fiscal_year: this.selectedYear,
          },
        },
      });

      // 集計情報を更新
      const resources = res.response.resources.rows;
      // リソースデータのemployment_statusをラベルに変換
      // APIレスポンスのemployment_statusは "PERMANENT", "TEMPORARY" などの値なので、ラベルに変換
      resources.forEach(resource => {
        if (resource.employment_status) {
          resource.employment_status = getEmploymentStatusLabel(
            resource.employment_status
          );
        }
      });
      const resourceStore = this.project.instance.resourceStore;
      const scheduler = this.$refs.scheduler.instance;

      // スケジューラAPIには retirement_date が含まれないため、crews API の船員一覧から退職日を取得してマージする
      const crewsList =
        this.crews && this.crews.crews && Array.isArray(this.crews.crews)
          ? this.crews.crews
          : [];
      // API レスポンスのリソース配列（resources.rows）に退職日をマージ
      for (const resource of resources) {
        const crewId = resource.id.split("_")[1];
        if (!crewId) continue;
        const crew = crewsList.find(c => c && c.id === crewId);
        if (crew) {
          resource.retirement_date = crew.retirement_date;
        }
      }
      // 表示用の resourceStore の各リーフ（船員）にも退職日をセット
      resourceStore.forEach(record => {
        if (!record.isLeaf) return;
        const crewId = record.id.split("_")[1];
        if (!crewId) return;
        const crew = crewsList.find(c => c && c.id === crewId);
        if (crew) {
          record.set("retirement_date", crew.retirement_date);
        }
      });

      updateSummariesToResource(resources, resourceStore, scheduler);

      // 船員リソースの表示順を crewResourceSorter でソート
      if (resourceStore && typeof resourceStore.sort === "function") {
        resourceStore.sort(crewResourceSorter);
      }

      // 退職日が今日より前の船員行に cls "retired-crew-row" を付与（既存の cls は維持）
      const today = moment().startOf("day");
      resourceStore.forEach(record => {
        if (!record.isLeaf) return;
        const retirementDateStr = record.retirement_date;
        if (!retirementDateStr) return;
        const retirementDate = moment(retirementDateStr).startOf("day");
        if (retirementDate.isValid() && retirementDate.isBefore(today)) {
          const baseCls = record.cls || "";
          const newCls = `${baseCls} retired-crew-row`.trim();
          record.set("cls", newCls);
        }
      });
      resourceStore.commit();
      // ソート・cls 変更を画面に反映
      if (scheduler && typeof scheduler.refresh === "function") {
        scheduler.refresh();
      }

      // 再ロードしてもカレンダーが変わらないので、再設定
      const { startDate, endDate } = res.response.project;
      this.currentStartDate = startDate;
      this.currentEndDate = endDate;

      await this.$refs.scheduler.instance.setTimeSpan(startDate, endDate);

      // 年度ラベルを更新
      this.$refs.scheduler.instance.widgetMap.yearLabel.value = this.selectedYear;
    },
  },

  async created() {
    // スケジューラの設定を作成
    this.schedulerConfig = createSchedulerConfig();
    // スケジューラプロのローカライズ設定
    LocaleManager.locale = LocaleHelper.locales.Ja;
  },

  async mounted() {
    // 現在日時を設定
    this.nowDate = new Date();

    // マスターデータの読み込み
    // 失敗した場合はダイアログを表示して、ページをリロードする。
    try {
      await Promise.all([
        (this.crews = await EnterpriseCrewsService.fetch()),
        (this.manningVessels = await EnterpriseManningVesselsService.fetch()),
        await EnterpriseCrewsBasicConditionsService.fetch(),
        await EnterpriseManningStatusService.fetch(),
        await EnterpriseCrewsFiscalYeatDefaultsService.fetch(),
        await EnterpriseCrewRoleService.fetch(),
        (this.schedulerColumns = await EnterpriseCrewSchedulerService.fetch()),
      ]);
    } catch (e) {
      this.$vs.dialog({
        type: "confirm",
        color: "danger",
        title: "システムエラー",
        text: "システムエラーが発生しました。管理者へお問い合わせください。",
        buttonCancel: true,
        acceptText: "OK",
      });
      return;
    }

    // カスタムタスク編集の設定
    this.selectableAhoreStausList = EnterpriseManningStatusService.getSelectableAshoreStatuses();
    this.taskEditFeature = CustomTaskEdit.buildCustomTaskEdit(
      this.selectableAhoreStausList
    );

    // 表示年度を設定してから、ツールバーを作成
    // 初回は現在の年度を設定する。
    const fisicalYear = EnterpriseCrewsBasicConditionsService.getFiscalYear(
      this.nowDate
    );
    this.selectedYear = fisicalYear;
    // ツールバーの作成
    const viewPresetsHandler = ViewPresetsHandler(this);
    const url = this.$router.resolve({
      name: "addon-crew-assignment-management-manning-schedule-vessels",
    }).href;
    const toolbar = Toolbar(
      "人単位 配乗表",
      this.selectedYear,
      "船舶単位",
      url,
      {
        onFiscalYearChange: this.onFiscalYearChange,
        onReloadClicked: this.reloadData,
        // カレンダースケールが変更されたときの処理
        onScaleChange: async presetId => {
          await viewPresetsHandler.onScaleChange(
            this.currentStartDate,
            this.currentEndDate,
            presetId
          );
        },
      }
    );
    this.tbar = toolbar.build();
    // イベントのツールチップ表示設定
    this.eventTooltipFeature = EventTooltipFeature().build();
    // イベントレンダラー設定
    this.eventRenderer = EventRenderer(C.DISPLAY_MODE.CREWS).build();

    // データ読み込み
    await this.reloadData();

    // 現在時刻の線を表示
    this.project.instance.applyChangeset({
      timeRanges: {
        added: [
          {
            id: "nowDate",
            startDate: moment(this.nowDate).format(),
            cls: "b-circle",
          },
        ],
      },
    });

    // カレンダーの初期表示位置の設定
    const visibleDate = getVisibleDate(this.currentStartDate, this.nowDate, 20);
    this.$refs.scheduler.instance.visibleDate = visibleDate;

    // リソースに集計情報列を追加
    this.$refs.scheduler.instance.columns.add([
      ...this.schedulerColumns.columns.map(col => ({
        text: col.text,
        htmlEncode: false,
        editor: false,
        readOnly: true,
        align: "center",
        children: col.children.map(child => ({
          text: child.text,
          field: child.field,
          autoWidth: true,
          readOnly: true,
          htmlEncode: false,
          editor: false,
          align: "right",
          sortable: false,
          filterable: false,
        })),
      })),
    ]);
    await this.$refs.scheduler.instance.subGrids.locked.collapse(false);

    // リソースのフィルター設定
    // 辞令職、氏名、社員区分のフィルターを設定
    const colmunStore = this.$refs.scheduler.instance.columns;
    const appointedRoleFilterList = [];
    const nameFilterList = [];
    const employmentStatusFilterList = [];
    this.$refs.scheduler.instance.resourceStore.data.forEach(resource => {
      appointedRoleFilterList.push(resource.appointed_role_name);
      nameFilterList.push(resource.name);
      employmentStatusFilterList.push(resource.employment_status);
    });
    colmunStore.get("appointed_role_name").data.filterable.filterField.items = [
      ...new Set(appointedRoleFilterList),
    ];
    colmunStore.get("name").data.filterable.filterField.items = [
      ...new Set(nameFilterList),
    ];
    colmunStore.get("employment_status").data.filterable.filterField.items = [
      ...new Set(employmentStatusFilterList),
    ];
  },
};
</script>
<style lang="scss" scoped>
@import "~@bryntum/schedulerpro/schedulerpro.stockholm.css";

.manning-schedule-crew-wrap {
  left: 0px;
  right: 0px;
  top: 0px;
  height: 100vh;
  position: absolute;
}

.manning-schedule-crew-wrap {
  :deep(.b-sch-event-content) {
    right: 0.75em !important;
    margin-left: auto !important;
  }

  /* カレンダーの土日色 */
  :deep(.calendar-saturday) {
    background-color: #b8b8ff;
    color: #fff;
  }

  :deep(.calendar-sunday) {
    background-color: #ffb8b8;
    color: #fff;
  }

  /* 現在日時線 */
  :deep(.b-grid-header .b-sch-line.b-circle.b-sch-timerange) {
    background-color: #ff0000;
    height: 0.8em !important;
    width: 0.8em !important;
    border-radius: 50%;
    margin-inline-start: -0.43em;
    bottom: 0;
    padding: 0;
  }

  :deep(.b-timeline-subgrid .b-sch-line.b-circle) {
    border-left: 2px solid #ff00003a;
  }

  :deep(.year-label) {
    width: 100px;
    height: 42px;
    font-size: 14px;
    color: #4f5964;
    background-color: white;
    border: rgb(208, 209, 210) solid 1px;
    border-radius: 2px;

    span {
      width: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }

  :deep(.b-grid-header-text) {
    padding: 0px;
  }

  /* 退職済み船員の視覚的区別（グリッド行のみ、イベントバーは除外） */
  /* 行背景色: #f5f5f5 */
  :deep(.b-grid-row.retired-crew-row),
  :deep(.b-grid-row.retired-crew-row .b-grid-cell) {
    background-color: #f5f5f5 !important;
  }

  /* テキストカラー: #999999 */
  :deep(.b-grid-row.retired-crew-row .b-grid-cell) {
    color: #999999 !important;
  }

  /* 3.3 視覚的な区切り（退職セクションの上にボーダー） */
  /* 非退職行の直後にある退職行 = 退職セクションの先頭行 */
  :deep(.b-grid-row:not(.retired-crew-row) + .retired-crew-row .b-grid-cell) {
    border-top: 2px solid #b0b0b0 !important;
  }

  /* [退職] バッジ */
  :deep(.retired-label) {
    display: inline-block;
    margin-left: 4px;
    padding: 0 6px;
    font-size: 0.8em;
    color: #666666 !important;
    background-color: #e0e0e0;
    border-radius: 4px;
  }
}
:deep(.b-grid-splitter[data-region="left"]) {
  pointer-events: none !important;
}
</style>
